import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Form, Row, Col, Container } from "react-bootstrap";
import FormInput from "../../components/ui/FormInput";
import { proposedLoanDetailsValidationSchema } from "../../utils/validationSchema";
import { proposedLoanDetailsFields, handleFileChange } from "../../utils/utils";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "../../components/shared/ErrorMessage";
// proposed_loan_details money columns store PAISE; this form edits in RUPEES.
import { toPaise, toRupees } from "../../utils/money";

// The money columns on proposed_loan_details, converted at both edges of this
// form. rateOfInterest and tenureInMonths are NOT money and stay as they are.
//
// Note the amounts are numbered one out of step with the agencies:
// fundingAgencyId pairs with fundingAmount2, fundingAgencyId2 with
// fundingAmount1. There is no fundingAgencyId1.
const MONEY_FIELDS = [
  "loanAmount",
  "fundingAmount1",
  "fundingAmount2",
  "fundingAmount3",
  "processingFee",
  "gstOnProcessingFee",
  "totalAmountPayable",
];

const calculateEmi = (loanAmount, tenureInMonths, rateOfInterest) => {
  if (!loanAmount || !tenureInMonths || !rateOfInterest) return null;

  const principal = parseFloat(loanAmount);
  const tenure = parseInt(tenureInMonths, 10);
  let annualInterestRate;

  if (rateOfInterest === "1.61") annualInterestRate = 19.32;
  else if (rateOfInterest === "1.41") annualInterestRate = 16.92;
  else if (rateOfInterest === "REGULAR") annualInterestRate = 24;
  else if (rateOfInterest === "24") annualInterestRate = 24;
  else if (rateOfInterest === "22") annualInterestRate = 22;
  else annualInterestRate = parseFloat(rateOfInterest);

  if (isNaN(annualInterestRate)) return null;

  const monthlyInterestRate = annualInterestRate / 12 / 100;

  const emi =
    (principal *
      monthlyInterestRate *
      Math.pow(1 + monthlyInterestRate, tenure)) /
    (Math.pow(1 + monthlyInterestRate, tenure) - 1);

  return Math.round(emi);
};

const ProposedLoanDetails = ({
  memberId,
  proposedLoanDetailsData,
  setProposedLoanDetailsData,
  personalDetailsData,
  nomineeDetailsData,
  handleNext,
  type,
  locked = false,
}) => {
  const navigate = useNavigate();
  const [isEditingRaw, setIsEditingRaw] = useState(type === "Edit");
  // `locked` wins over this component's own isEditing. Passing type="View" is
  // NOT enough: the Edit button below renders *because* the type is View, and
  // clicking it sets isEditing = true, which would reopen a disbursed
  // application. Defaults to false so the Customers flow is unaffected.
  const isEditing = locked ? false : isEditingRaw;
  const setIsEditing = (v) => { if (!locked) setIsEditingRaw(v); };
  const [errorMsg, setErrorMsg] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
    reset,
    control,
  } = useForm({
    defaultValues: proposedLoanDetailsData,
    // Without a resolver this form validated nothing: FormInput calls
    // register(name) with no rules, so `required: true` in utils.js only drew a
    // red asterisk. A loan could be saved with no EMI start date, which makes an
    // EMI schedule impossible to generate — and the disbursement path does not
    // check for a schedule, so the money left anyway.
    resolver: yupResolver(proposedLoanDetailsValidationSchema),
  });

  const [filePreviews, setFilePreviews] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [fieldsData, setFieldsData] = useState([]);

  useEffect(() => {
    const fetchFieldsData = async () => {
      const data = await proposedLoanDetailsFields();
      setFieldsData(data);
    };
    fetchFieldsData();
  }, []);

  useEffect(() => {
    if (proposedLoanDetailsData && Object.keys(proposedLoanDetailsData).length > 0) {
      // Stored PAISE -> the RUPEES this form edits in. Without this a saved
      // Rs.1,00,000 loan would reopen showing 10000000, and re-saving it would
      // multiply by 100 again.
      const inRupees = { ...proposedLoanDetailsData };
      for (const key of MONEY_FIELDS) {
        if (inRupees[key] !== undefined && inRupees[key] !== null && inRupees[key] !== "") {
          inRupees[key] = toRupees(inRupees[key]);
        }
      }

      reset(inRupees);
      Object.keys(inRupees).forEach((key) => {
        setValue(key, inRupees[key]);
        if (key.endsWith("Image") && proposedLoanDetailsData[key]) {
          setExistingFiles((prev) => ({
            ...prev,
            [key]: `${API_BASE_URL}/images/${proposedLoanDetailsData[key]}`,
          }));
        }
      });
    }

    // Auto-fill logic
    const currentValues = getValues();
    const isApplicantEmpty = !currentValues.applicantName || currentValues.applicantName === "0" || currentValues.applicantName === 0;
    const isCoApplicantEmpty = !currentValues.coApplicant1 || currentValues.coApplicant1 === "0" || currentValues.coApplicant1 === 0;

    console.log("Auto-fill evaluation:", {
      isApplicantEmpty,
      isCoApplicantEmpty,
      personalName: personalDetailsData?.name,
      nomineeName: nomineeDetailsData?.name
    });

    if (isApplicantEmpty && personalDetailsData?.name) {
      setValue("applicantName", personalDetailsData.name, { shouldDirty: true, shouldValidate: true });
    }
    if (isCoApplicantEmpty && nomineeDetailsData?.name) {
      setValue("coApplicant1", nomineeDetailsData.name, { shouldDirty: true, shouldValidate: true });
    }
  }, [proposedLoanDetailsData, personalDetailsData, nomineeDetailsData, setValue, reset, getValues]);

  // Watch fields for calculations
  const loanAmount = watch("loanAmount");
  const tenureInMonths = watch("tenureInMonths");
  const rateOfInterest = watch("rateOfInterest");

  useEffect(() => {
    // Calculate EMI
    const emi = calculateEmi(loanAmount, tenureInMonths, rateOfInterest);
    setValue("monthlyEmi", emi || null);

    // Calculate Processing Fee and Total Amount if not provided by backend
    if (loanAmount) {
      const loanAmt = parseFloat(loanAmount) || 0;
      const processingFee = loanAmt * 0.02; // 2% of loan amount
      const gstOnProcessingFee = processingFee * 0.18; // 18% of processing fee
      const totalAmountPayable = loanAmt - processingFee - gstOnProcessingFee;

      setValue("processingFee", processingFee.toFixed(2), { shouldDirty: true });
      setValue("gstOnProcessingFee", gstOnProcessingFee.toFixed(2), { shouldDirty: true });
      setValue("totalAmountPayable", totalAmountPayable.toFixed(2), { shouldDirty: true });
    }
  }, [loanAmount, tenureInMonths, rateOfInterest, setValue]);

  // Track the proposed_loan_details record ID separately so it never gets lost
  const recordId = proposedLoanDetailsData?.id;

  const onSubmit = async (submittedData) => {
    // Payload for proposed_loan_details
    //
    // Insert or update is decided by WHETHER THIS SECTION EXISTS (recordId), not
    // by the URL's `type`. That flag describes the loan, and sending type:"Edit"
    // with an absent id makes JSON.stringify drop the key, so the server refuses
    // with "Update requires a valid ID."
    const isNew = !recordId;

    const { id: _ignoredId, ...fields } = submittedData;

    // The form works in RUPEES; these columns store PAISE (Stage 2 of the money
    // conversion). Converted here at the submit edge so every calculation above —
    // the EMI, the 2% processing fee, the 18% GST — stays in rupees and unchanged.
    //
    // Left in rupees deliberately: rateOfInterest and tenureInMonths are not money.
    for (const key of MONEY_FIELDS) {
      if (fields[key] !== undefined && fields[key] !== null && fields[key] !== "") {
        fields[key] = toPaise(fields[key]);
      }
    }

    const loanDetailsPayload = {
      ...fields,
      model: "proposed_loan_details",
      type: isNew ? "Add" : "Edit",
      // Required, or the new row is orphaned with no loan.
      memberId,
    };
    if (!isNew) loanDetailsPayload.id = recordId;

    console.log("ProposedLoanDetails payload:", loanDetailsPayload);

    // Payload for member_details — include adminStatus to trigger loan chart generation
    const memberDetailsPayload = {
      model: "member_details",
      type: "Edit",
      id: memberId,
      // amountToRelease is deliberately not sent. It holds the SANCTIONED amount
      // and is maintained by addDetails.js, which stamps in
      // proposed_loan_details.loanAmount on any member_details edit. Sending a
      // value here would be overwritten anyway.
    };

    try {
      // Execute both API calls concurrently
      const [loanDetailsResponse, memberDetailsResponse] = await Promise.all([
        axios.post(`${API_BASE_URL}/api/addDetails`, loanDetailsPayload),
        axios.post(`${API_BASE_URL}/api/addDetails`, memberDetailsPayload),
      ]);

      // Handle proposed_loan_details response
      if (loanDetailsResponse.status === 201) {
        // Preserve existing data (including id) and merge with submitted data
        setProposedLoanDetailsData(prev => ({
          ...prev,
          ...submittedData,
        }));
        console.log(
          "Proposed Loan Details updated successfully: ",
          loanDetailsResponse.data
        );
        setIsEditing(false);

        if (type === "Add") {
          handleNext();
        }
        if (isEditing || type === "Edit") {
          navigate("/addEditBusinessLoan", {
            state: { type: "View", stepNo: 7, memberId },
          });
        }
      } else {
        setErrorMsg(loanDetailsResponse.data.error || memberDetailsResponse.data.error || "Error updating details");
      }
    } catch (error) {
      console.error("Error updating details: ", error);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Error updating details";
      setErrorMsg(msg);
    }
  };

  const onError = (errors) => {
    const errorMessages = Object.keys(errors).reduce((acc, key) => {
      acc[key] = errors[key].message;
      return acc;
    }, {});
    console.log("Form errors:", errorMessages);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset(proposedLoanDetailsData);
    setFilePreviews({});
  };

  return (
    <Container className="mt-4">
      <h2 className="text-center mb-4">
        Proposed Loan Details
        {!locked && type === "View" && !isEditing && (
          <Button
            variant="primary"
            className="ms-3 mt-2 btn-hover"
            onClick={handleEditClick}
          >
            Edit
          </Button>
        )}
      </h2>
      <ErrorMessage message={errorMsg} />
      <Form onSubmit={handleSubmit(onSubmit, onError)}>
        <Row>
          {fieldsData.map((field, index) => {
            let label = field.label;
            let fieldType = field.type;
            let placeholder = "";
            let valueFormat;

            // Dynamic label for funding amount based on agency selection
            if (field.name === "fundingAmount2") {
              const selectedAgencyId = watch("fundingAgencyId");
              const fundingAgencyField = fieldsData.find(f => f.name === "fundingAgencyId");
              const agency = fundingAgencyField?.options?.find(opt => opt.value === selectedAgencyId);
              if (agency) {
                label = `${agency.label}`;
              }
            } else if (field.name === "fundingAmount1") {
              const selectedAgencyId = watch("fundingAgencyId2");
              const fundingAgencyField = fieldsData.find(f => f.name === "fundingAgencyId2");
              const agency = fundingAgencyField?.options?.find(opt => opt.value === selectedAgencyId);
              if (agency) {
                label = `${agency.label}`;
              }
            } else if (field.name === "fundingAmount3") {
              const selectedAgencyId = watch("fundingAgencyId3");
              const fundingAgencyField = fieldsData.find(f => f.name === "fundingAgencyId3");
              const agency = fundingAgencyField?.options?.find(opt => opt.value === selectedAgencyId);
              if (agency) {
                label = `${agency.label}`;
              }
            }

            // Loan Date formatting
            if (field.name === "emiDate") {
              fieldType = "datepicker";
              // DD.MM.YYYY is the only format adminLoanController.parseEuropeanDate
              // accepts, and what proposedLoanDetailsValidationSchema enforces.
              // Without this the picker emits YYYY-MM-DD and the field can never
              // be satisfied from the calendar.
              valueFormat = "dot";
              placeholder = "09.04.2026";
            }

            return (
              <Col
                xs={12}
                md={6}
                key={index}
                className="mb-3"
              >
                <FormInput
                  label={label}
                  type={fieldType}
                  valueFormat={valueFormat}
                  name={field.name}
                  register={register}
                  watch={watch}
                  errors={errors}
                  options={field.options || []}
                  readOnly={(type === "View" && !isEditing) || field.readOnly}
                  handleFileChange={(e) =>
                    handleFileChange(e, field.name, setFilePreviews)
                  }
                  existingFileUrl={existingFiles[field.name] || ""}
                  filePreview={filePreviews[field.name]?.previewUrl || ""}
                  required={field.required}
                  placeholder={placeholder}
                  control={control}
                  minDate={null}
                />
              </Col>
            );
          })}
        </Row>

        <div className="text-center mt-3">
          {(type !== "View" || isEditing) && (
            <Button variant="success" type="submit" className="me-2">
              {isEditing || type === "Edit" ? "Update" : "Submit"}
            </Button>
          )}
          {(isEditing || type === "Edit") && (
            <Button
              variant="warning"
              onClick={handleCancelEdit}
              className="me-2"
            >
              Cancel
            </Button>
          )}
          {!locked && type === "View" && !isEditing && (
            <Button
              variant="secondary"
              onClick={() =>
                navigate("/addEditBusinessLoan", {
                  state: { type: "View", stepNo: 7, memberId },
                })
              }
            >
              Back
            </Button>
          )}
        </div>
      </Form>
    </Container>
  );
};

export default ProposedLoanDetails;